import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { PresetController } from "../helper/controllers/preset-controller";

export interface SkyGridColumnsConfig {
  columns: number;
  lg: number;
  md: number;
  sm: number;
  xs: number;
}

/**
 * @element sky-grid
 *
 * @summary Responsive CSS grid container with configurable breakpoint column counts.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/grid
 *
 * @slot - Default slot for grid items (direct children become grid items).
 * @property {number} columns - Base number of grid columns.
 * @property {string} gap - Gap between grid items.
 * @property {number} lg - Column count at large breakpoints.
 * @property {number} md - Column count at medium breakpoints.
 * @property {number} sm - Column count at small breakpoints.
 * @property {number} xs - Column count at extra-small breakpoints.
 * @property {boolean} nowrap - Reuses `columns` across breakpoints when true.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @csspart grid - The grid container element.
 *
 * @Behavior
 * - Uses CSS variables for runtime breakpoint column updates
 * - Supports explicit per-breakpoint column counts
 * - Supports `nowrap` mode to keep a fixed column count
 *
 * @example
 * ```html
 * <sky-grid columns="12" lg="4" md="3" sm="2" xs="1" gap="16px">
 *   <sky-card>Card 1</sky-card>
 *   <sky-card>Card 2</sky-card>
 *   <sky-card>Card 3</sky-card>
 *   <sky-card>Card 4</sky-card>
 * </sky-grid>
 * ```
 * ```vue
 * <template>
 *   <sky-grid columns="12" lg="4" md="3" sm="2" xs="1" gap="16px">
 *     <sky-card>Card 1</sky-card>
 *     <sky-card>Card 2</sky-card>
 *     <sky-card>Card 3</sky-card>
 *     <sky-card>Card 4</sky-card>
 *   </sky-grid>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-grid columns="12" lg="4" md="3" sm="2" xs="1" gap="16px">
 *       <sky-card>Card 1</sky-card>
 *       <sky-card>Card 2</sky-card>
 *       <sky-card>Card 3</sky-card>
 *       <sky-card>Card 4</sky-card>
 *     </sky-grid>
 *   );
 * }
 * ```
 */
@customElement("sky-grid")
export class SkyGrid extends LitElement {

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: block;
    }

    .grid {
      display: grid;
      gap: var(--grid-gap, 1rem);
      grid-template-columns: repeat(var(--grid-columns, 4), 1fr);
    }

    @media (max-width: 1200px) {
      .grid {
        grid-template-columns: repeat(var(--grid-columns-lg), 1fr);
      }
    }

    @media (max-width: 992px) {
      .grid {
        grid-template-columns: repeat(var(--grid-columns-md), 1fr);
      }
    }

    @media (max-width: 768px) {
      .grid {
        grid-template-columns: repeat(var(--grid-columns-sm), 1fr);
      }
    }

    @media (max-width: 576px) {
      .grid {
        grid-template-columns: repeat(var(--grid-columns-xs), 1fr);
      }
    }
  `;

  @property({ type: Number }) columns = 12;
  @property({ type: String }) gap = "10px";
  @property({ type: Number }) lg = 3;
  @property({ type: Number }) md = 2;
  @property({ type: Number }) sm = 2;
  @property({ type: Number }) xs = 1;
  @property({ type: Boolean, reflect: true }) nowrap = false;

  override updated(changedProperties: Map<string | number | symbol, unknown>): void {
    if (
      changedProperties.has("nowrap") ||
      changedProperties.has("lg") ||
      changedProperties.has("md") ||
      changedProperties.has("sm") ||
      changedProperties.has("xs") ||
      changedProperties.has("columns")
    ) {
      this.style.setProperty("--grid-columns", this.columns.toString());
      if (this.nowrap) {
        this.style.setProperty("--grid-columns-lg", this.columns.toString());
        this.style.setProperty("--grid-columns-md", this.columns.toString());
        this.style.setProperty("--grid-columns-sm", this.columns.toString());
        this.style.setProperty("--grid-columns-xs", this.columns.toString());
      } else {
        this.style.setProperty(
          "--grid-columns-lg",
          (this.lg || this.columns).toString(),
        );
        this.style.setProperty(
          "--grid-columns-md",
          (this.md || this.columns).toString(),
        );
        this.style.setProperty(
          "--grid-columns-sm",
          (this.sm || this.columns).toString(),
        );
        this.style.setProperty(
          "--grid-columns-xs",
          (this.xs || this.columns).toString(),
        );
      }
    }
    if (changedProperties.has("gap")) {
      this.style.setProperty("--grid-gap", this.gap);
    }
  }

  override render() {
    return html`
      <div class="grid">
        <slot></slot>
      </div>
    `;
  }
}
